import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IApplicationState } from '../../../../../types/redux/store';
import { Desktop, NotDesktop } from '../../../../../../packages/Responsive';
import { MapLayer } from '../../../../../components/MapLayer';
import { preparePolygons } from '../../../../../mappers/polygons';
import { finishDrawing, startDrawing } from '../../../../../actions/drawing';
import { clearGeoType } from '../../../../../actions/jsonQuery';
import { OnboardingTooltip } from '../../../../../components/OnboardingTooltip';
import { COOKIE_NAME } from './constants';
import { setOnboardingCloseCookie } from './utils';
import { DrawButton } from './DrawButton';
import { trackAreaSelect } from './tracking';
import * as styles from './styles.css';

export const DrawButtonContainer: React.FC = () => {
  const dispatch = useDispatch();
  const isDrawingMode = useSelector((state: IApplicationState) => state.drawing.isDrawingMode);
  const polygons = useSelector((state: IApplicationState) => preparePolygons(state.filters.jsonQuery));
  const [shouldShowOnboarding, setShouldShowOnboarding] = React.useState(false);
  const onboardingTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasPolygons = polygons.length > 0;
  const isActive = (!hasPolygons && isDrawingMode) || shouldShowOnboarding;

  const hideOnboarding = React.useCallback(() => {
    setShouldShowOnboarding(false);
    setOnboardingCloseCookie();
  }, []);

  const showOnboarding = React.useCallback(() => {
    setShouldShowOnboarding(true);

    onboardingTimeoutRef.current = setTimeout(hideOnboarding, 5000);
  }, [hideOnboarding]);

  const handleActivateClick = React.useCallback(() => {
    hideOnboarding();

    if (!isDrawingMode) {
      dispatch(startDrawing());
      trackAreaSelect('start');
    } else {
      dispatch(finishDrawing());
      trackAreaSelect('close');
    }
  }, [dispatch, hideOnboarding, isDrawingMode]);

  const handleCancelClick = React.useCallback(() => {
    hideOnboarding();

    dispatch(finishDrawing());
    dispatch(clearGeoType('polygon'));
    trackAreaSelect('close');
  }, [dispatch, hideOnboarding]);

  React.useEffect(() => {
    if (document.cookie.indexOf(COOKIE_NAME) === -1) {
      onboardingTimeoutRef.current = setTimeout(showOnboarding, 2000);
    }

    return () => {
      if (onboardingTimeoutRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(onboardingTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Desktop>
        <OnboardingTooltip tooltip="Выделяйте интересующую вас область на карте" open={shouldShowOnboarding}>
          <DrawButton
            active={isActive}
            hasPolygons={hasPolygons}
            onActivateClick={handleActivateClick}
            onCancelClick={handleCancelClick}
          />
        </OnboardingTooltip>
      </Desktop>
      <NotDesktop>
        <MapLayer className={styles['layer']}>
          <DrawButton
            active={isActive}
            hasPolygons={hasPolygons}
            iconSizeModifier={hasPolygons ? 'L' : 'XL'}
            onActivateClick={handleActivateClick}
            onCancelClick={handleCancelClick}
            size="XL"
            rounded
          />
        </MapLayer>
      </NotDesktop>
    </>
  );
};
